import api from './api';
import { handleError } from '../utils/errorHandler';
import { FundoInvestimento, RendaFixaBancario, RendaFixaTituloPublico, Segmento, TipoAtivo, ADR, Compromissada, RendaFixaCorporativo, RendaVariavel, RendaFixaSecuritizado } from '../types/ativos';

// Função para obter a lista de segmentos
export const getSegmentos = async () => {
  try {
    const response = await api.get<{ results: Segmento[] }>('/ativos/segmento/');
    return response.data.results;
  } catch (error) {
    handleError(error, 'ao buscar segmentos');
    throw error;
  }
};

export const createSegmento = async (segmento: { nome: string }) => {
  try {
    const response = await api.post('/ativos/segmento/', segmento);
    return response.data;
  } catch (error) {
    handleError(error, 'ao criar segmento');
    throw error;
  }
};

export const createTipoAtivo = async (tipoAtivo: { nome: string }) => {
  try {
    const response = await api.post('/ativos/tipo-ativo/', tipoAtivo);
    return response.data;
  } catch (error) {
    handleError(error, 'ao criar tipo de ativo');
    throw error;
  }
};

export const getAtivos = async () => {
  try {
    const response = await api.get<{ results: { id: number; nome: string }[] }>('/ativos/ativos/');
    return response.data.results;
  } catch (error) {
    handleError(error, 'ao buscar ativos');
    throw error;
  }
};

// Função para obter a lista de tipos de ativo
export const getTiposAtivo = async () => {
  try {
    const response = await api.get<{ results: TipoAtivo[] }>('/ativos/tipo-ativo/');
    return response.data.results;
  } catch (error) {
    handleError(error, 'ao buscar tipos de ativo');
    throw error;
  }
};

// Função para obter a lista de benchmarks
export const getBenchmarks = async () => {
  try {
    const response = await api.get('/core/benchmarks/');
    return response.data.results;
  } catch (error) {
    handleError(error, 'ao buscar benchmarks');
    throw error;
  }
};

// Função para obter a lista de tributações
export const getTributacoes = async () => {
  try {
    const response = await api.get('/core/tributacoes/');
    return response.data.results;
  } catch (error) {
    handleError(error, 'ao buscar tributações');
    throw error;
  }
};

// Função para obter a lista de nacionalidades
export const getNacionalidades = async () => {
  try {
    const response = await api.get('/core/nacionalidades/');
    return response.data.results;
  } catch (error) {
    handleError(error, 'ao buscar nacionalidades');
    throw error;
  }
};

// Função para obter a lista de condomínios
export const getCondominios = async () => {
  try {
    const response = await api.get('/core/condominios/');
    return response.data.results;
  } catch (error) {
    handleError(error, 'ao buscar condomínios');
    throw error;
  }
};

// Função para obter a lista de distribuições
export const getDistribuicoes = async () => {
  try {
    const response = await api.get('/core/distribuicoes/');
    return response.data.results;
  } catch (error) {
    handleError(error, 'ao buscar distribuições');
    throw error;
  }
};

// Função para obter a lista de captações
export const getCaptacoes = async () => {
  try {
    const response = await api.get('/core/captacoes/');
    return response.data.results;
  } catch (error) {
    handleError(error, 'ao buscar captações');
    throw error;
  }
};

// Função para obter a lista de status
export const getStatus = async () => {
  try {
    const response = await api.get('/core/status/');
    return response.data.results;
  } catch (error) {
    handleError(error, 'ao buscar status');
    throw error;
  }
};

// Função para cadastrar um ativo
export const cadastrarAtivo = async (ativo: FundoInvestimento) => {
  try {
    const response = await api.post('/ativos/ativo-create/', ativo);
    return response.data;
  } catch (error) {
    handleError(error, 'ao cadastrar ativo');
    throw error;
  }
};

export const getFundosInvestimento = async (
  pageIndex: number,
  pageSize: number = 100,
  sortBy: any[] = [],
  globalFilter: string = ''
) => {
  try {
    const sortParam = sortBy.length
      ? `${sortBy.map(sort => (sort.desc ? '-' : '') + sort.id).join(',')}`
      : '';

    const searchParam = globalFilter ? `&search=${globalFilter}` : '';

    const response = await api.get<{ results: FundoInvestimento[], count: number }>(
      `/ativos/fundos/?page=${pageIndex + 1}&page_size=${pageSize}&ordering=${sortParam}${searchParam}`
    );

    return {
      results: response.data.results,
      totalPages: Math.ceil(response.data.count / pageSize),
    };
  } catch (error) {
    handleError(error, 'ao buscar fundos de investimento');
    throw error;
  }
};

export const updateAtivo = async (segmentoNome: string, id: number, data: any) => {
  try {
    const response = await api.patch(`/ativos/${segmentoNome}/${id}/`, data);
    return response.data;
  } catch (error) {
    handleError(error, 'ao atualizar ativo');
    throw error;
  }
};

export const getRendaFixaTitulosPublicos = async (
  pageIndex: number,
  pageSize: number = 100,
  sortBy: any[] = [],
  globalFilter: string = ''
) => {
  try {
    const sortParam = sortBy.length
      ? `${sortBy.map(sort => (sort.desc ? '-' : '') + sort.id).join(',')}`
      : '';

    const searchParam = globalFilter ? `&search=${globalFilter}` : '';

    const response = await api.get<{ results: RendaFixaTituloPublico[], count: number }>(
      `/ativos/renda-fixa-titulos-publicos/?page=${pageIndex + 1}&page_size=${pageSize}&ordering=${sortParam}${searchParam}`
    );

    return {
      results: response.data.results,
      totalPages: Math.ceil(response.data.count / pageSize),
    };
  } catch (error) {
    handleError(error, 'ao buscar títulos públicos');
    throw error;
  }
};

export const getCompromissadas = async (
  pageIndex: number,
  pageSize: number = 100,
  sortBy: any[] = [],
  globalFilter: string = ''
) => {
  try {
    const sortParam = sortBy.length
      ? `${sortBy.map(sort => (sort.desc ? '-' : '') + sort.id).join(',')}`
      : '';

    const searchParam = globalFilter ? `&search=${globalFilter}` : '';

    const response = await api.get<{ results: Compromissada[], count: number }>(
      `/ativos/compromissada/?page=${pageIndex + 1}&page_size=${pageSize}&ordering=${sortParam}${searchParam}`
    );

    return {
      results: response.data.results,
      totalPages: Math.ceil(response.data.count / pageSize),
    };
  } catch (error) {
    handleError(error, 'ao buscar compromissadas');
    throw error;
  }
};

export const getRendaFixaBancario = async (
  pageIndex: number,
  pageSize: number = 100,
  sortBy: any[] = [],
  globalFilter: string = ''
) => {
  try {
    const sortParam = sortBy.length
      ? `${sortBy.map(sort => (sort.desc ? '-' : '') + sort.id).join(',')}`
      : '';

    const searchParam = globalFilter ? `&search=${globalFilter}` : '';

    const response = await api.get<{ results: RendaFixaBancario[], count: number }>(
      `/ativos/renda-fixa-bancario/?page=${pageIndex + 1}&page_size=${pageSize}&ordering=${sortParam}${searchParam}`
    );

    return {
      results: response.data.results,
      totalPages: Math.ceil(response.data.count / pageSize),
    };
  } catch (error) {
    handleError(error, 'ao buscar renda fixa bancário');
    throw error;
  }
};

export const getRendaFixaCorporativo = async (
  pageIndex: number,
  pageSize: number = 100,
  sortBy: any[] = [],
  globalFilter: string = ''
) => {
  try {
    const sortParam = sortBy.length
      ? `${sortBy.map(sort => (sort.desc ? '-' : '') + sort.id).join(',')}`
      : '';

    const searchParam = globalFilter ? `&search=${globalFilter}` : '';

    const response = await api.get<{ results: RendaFixaCorporativo[], count: number }>(
      `/ativos/renda-fixa-corporativo/?page=${pageIndex + 1}&page_size=${pageSize}&ordering=${sortParam}${searchParam}`
    );

    return {
      results: response.data.results,
      totalPages: Math.ceil(response.data.count / pageSize),
    };
  } catch (error) {
    handleError(error, 'ao buscar renda fixa corporativo');
    throw error;
  }
};

export const getRendaFixaSecuritizado = async (
  pageIndex: number,
  pageSize: number = 100,
  sortBy: any[] = [],
  globalFilter: string = ''
) => {
  try {
    const sortParam = sortBy.length
      ? `${sortBy.map(sort => (sort.desc ? '-' : '') + sort.id).join(',')}`
      : '';

    const searchParam = globalFilter ? `&search=${globalFilter}` : '';

    const response = await api.get<{ results: RendaFixaSecuritizado[], count: number }>(
      `/ativos/renda-fixa-securitizado/?page=${pageIndex + 1}&page_size=${pageSize}&ordering=${sortParam}${searchParam}`
    );

    return {
      results: response.data.results,
      totalPages: Math.ceil(response.data.count / pageSize),
    };
  } catch (error) {
    handleError(error, 'ao buscar renda fixa securitizado');
    throw error;
  }
};

export const getRendaVariavel = async (
  pageIndex: number,
  pageSize: number = 100,
  sortBy: any[] = [],
  globalFilter: string = ''
) => {
  try {
    const sortParam = sortBy.length
      ? `${sortBy.map(sort => (sort.desc ? '-' : '') + sort.id).join(',')}`
      : '';

    const searchParam = globalFilter ? `&search=${globalFilter}` : '';

    const response = await api.get<{ results: RendaVariavel[], count: number }>(
      `/ativos/renda-variavel/?page=${pageIndex + 1}&page_size=${pageSize}&ordering=${sortParam}${searchParam}`
    );

    return {
      results: response.data.results,
      totalPages: Math.ceil(response.data.count / pageSize),
    };
  } catch (error) {
    handleError(error, 'ao buscar renda variável');
    throw error;
  }
};

export const getADR = async (
  pageIndex: number,
  pageSize: number = 100,
  sortBy: any[] = [],
  globalFilter: string = ''
) => {
  try {
    const sortParam = sortBy.length
      ? `${sortBy.map(sort => (sort.desc ? '-' : '') + sort.id).join(',')}`
      : '';

    const searchParam = globalFilter ? `&search=${globalFilter}` : '';

    const response = await api.get<{ results: ADR[], count: number }>(
      `/ativos/adr/?page=${pageIndex + 1}&page_size=${pageSize}&ordering=${sortParam}${searchParam}`
    );

    return {
      results: response.data.results,
      totalPages: Math.ceil(response.data.count / pageSize),
    };
  } catch (error) {
    handleError(error, 'ao buscar ADR');
    throw error;
  }
};

export const getOpcoesDescricao = async () => {
  try {
    const response = await api.get<{ results: { id: number; descricao: string }[] }>('/ativos/opcoes_descricao/');
    return response.data.results;
  } catch (error) {
    handleError(error, 'ao buscar opções de descrição');
    throw error;
  }
};

// Função para criar uma nova opção de descrição
export const createOpcaoDescricao = async (descricao: string) => {
  try {
    const response = await api.post('/ativos/opcoes_descricao/', { descricao });
    return response.data;
  } catch (error) {
    handleError(error, 'ao criar nova opção de descrição');
    throw error;
  }
};

// Função para excluir uma opção de descrição
export const deleteOpcaoDescricao = async (id: number) => {
  try {
    await api.delete(`/ativos/opcoes_descricao/${id}/`);
  } catch (error) {
    handleError(error, 'ao excluir opção de descrição');
    throw error;
  }
};

export const getPerfisEmpresa = async () => {
  try {
    const response = await api.get<{ results: { id: number; descricao: string }[] }>('/ativos/perfis_empresa/');
    return response.data.results;
  } catch (error) {
    handleError(error, 'ao buscar perfis de empresa');
    throw error;
  }
};

// Função para criar uma nova opção de descrição
export const createPerfisEmpresa = async (descricao: string) => {
  try {
    const response = await api.post('/ativos/perfis_empresa/', { descricao });
    return response.data;
  } catch (error) {
    handleError(error, 'ao criar perfil de empresa');
    throw error;
  }
};

// Função para excluir uma opção de descrição
export const deletePerfisEmpresa = async (id: number) => {
  try {
    await api.delete(`/ativos/perfis_empresa/${id}/`);
  } catch (error) {
    handleError(error, 'ao excluir perfil de empresa');
    throw error;
  }
};

export const saveFundoInvestimentoRegras = async (fundoId: number, data: any) => {
  try {
    const response = await api.put(`/ativos/fundo_regras/${fundoId}/`, data);
    return response.data;
  } catch (error) {
    handleError(error, 'ao salvar as regras do fundo');
    throw error;
  }
};

// Função para atualizar regras de fundo de investimento existente
export const atualizarFundoRegras = async (id: number, dados: any) => {
  try {
    const response = await api.put(`/ativos/fundo_regras/${id}/`, dados);
    return response.data;
  } catch (error) {
    handleError(error, 'ao atualizar regras do fundo de investimento');
    throw error;
  }
};

export const getFundoInvestimentoRegras = async (fundoId: number) => {
  try {
    const response = await api.get(`/ativos/fundo_regras/${fundoId}/`);
    return response.data;
  } catch (error) {
    handleError(error, 'ao buscar regras do fundo de investimento');
    throw error;
  }
};

export const getDescricaoAtivo = async () => {
  try {
    const response = await api.get<{ results: { id: number; descricao: string }[] }>('/ativos/descricao_ativo/');
    return response.data.results;
  } catch (error) {
    handleError(error, 'ao buscar descrições de ativo');
    throw error;
  }
};

// Função para criar uma nova opção de descrição
export const createDescricaoAtivo = async (descricao: string) => {
  try {
    const response = await api.post('/ativos/descricao_ativo/', { descricao });
    return response.data;
  } catch (error) {
    handleError(error, 'ao criar descrição de ativo');
    throw error;
  }
};

export const saveLegislacaoAtivos = async (legislacaoId: number, data: any) => {
  try {
    const response = await api.put(`/ativos/legislacao_ativos/${legislacaoId}/`, data);
    return response.data;
  } catch (error) {
    handleError(error, 'ao salvar legislação de ativos');
    throw error;
  }
};

export const getLegislacaoAtivos = async (ativoId: number) => {
  try {
    const response = await api.get(`/ativos/legislacao_ativos/${ativoId}/`);
    return response.data;
  } catch (error) {
    handleError(error, 'ao buscar legislação do ativo');
    throw error;
  }
};
